import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Client } from '@app/core/model/client/client';
import { User } from '@app/core/model/client/user';
import { UsersService } from '@app/shared/services/users.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { PopupManager } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { Observable, tap } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'invitation-cell-renderer',
  templateUrl: './invitation-cell-renderer.component.html',
  styleUrls: ['./invitation-cell-renderer.component.scss'],
  standalone: false
})
export class InvitationCellRendererComponent implements ICellRendererAngularComp {
  private usersService: UsersService = inject(UsersService);
  private snackbarManager: SnackbarManager = inject(SnackbarManager);
  private translate: TranslateService = inject(TranslateService);
  private popupManager: PopupManager = inject(PopupManager);
  private destroyRef = inject(DestroyRef);
  private accessManager = inject(AccessManager);

  public user: User;
  public client?: Client;

  public state: 'none' | 'accepted' | 'sending' | 'sent';
  public canSend: boolean = false;
  public readonly Permission = PermissionEnum;

  /**
   * Initialize the component's data and state from AG-Grid's parameters.
   * @param params CellRenderer parameters.
   */
  public agInit(params: ICellRendererParams<User, boolean> & { client?: Client }): void {
    this.refresh(params);
  }

  /**
   * Refresh the component's data and state.
   * @param params CellRenderer parameters.
   * @return true.
   */
  public refresh(params: ICellRendererParams<User, boolean | null> & { client?: Client }): boolean {
    this.user = params.data;
    this.client = params.client;
    if (params.value === true) {
      // User has received and accepted an invitation
      this.state = 'accepted';
    } else if (params.value === false) {
      // User has received an invitation but has not accepted it yet, they may be sent a new one after 1 hour
      this.state = 'sent';
      this.canSend = !!this.client && this.user.lastInvite?.isBefore(dayjs().subtract(1, 'hour'));
    } else {
      // User has not been sent an invitation yet, super admins may send one manually
      this.state = 'none';
      this.canSend = !!this.client && this.accessManager.isSuperAdmin();
    }
    return true;
  }

  /**
   * Display a confirmation dialog, then call the UsersService to send an invitation to the user upon confirmation.
   * Do nothing if the dialog is canceled. The passed MouseEvent will be stopped from propagating.
   * @param event Click event.
   */
  public onClickSend(event: MouseEvent) {
    event.stopPropagation();
    this.popupManager.showOkCancelPopup({
      type: 'warning',
      dialogTitle: this.translate.instant('TITLE.SEND_INVITATION'),
      dialogMessage: this.translate.instant('MESSAGE.SEND_INVITATION', {name: this.user.name})
    })
      .afterClosed()
      .pipe(
        filter(response => response === 'yes'),
        switchMap(() => this.sendInvitation())
      )
      .subscribe();
  }

  /**
   * Call the UsersService to send an invitation to the User. The passed MouseEvent will be stopped from propagating.
   * @param event Click event.
   */
  public onClickResend(event: MouseEvent) {
    event.stopPropagation();
    this.sendInvitation().subscribe();
  }

  /**
   * Call the UsersService to send an invitation to the user.
   * @return Observable emitting the updated user.
   * @private
   */
  private sendInvitation(): Observable<User> {
    this.state = 'sending';
    this.canSend = false;
    return this.usersService.sendInvitation(this.user, this.client)
      .pipe(
        tap(() => {
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.INVITATION_SENT'));
          this.state = 'sent';
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }
}
